import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, Grid, Key } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
  digramsProcessed: number;
  totalSquaresUsed: number;
  uniqueKeywords: Set<string>;
  averageKeywordLength: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  keyword1: string;
  keyword2: string;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  textLength: number;
  digramsCount: number;
  orientation: 'horizontal' | 'vertical';
}

const TwoSquareCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [keyword1, setKeyword1] = useState<string>('EXAMPLE');
  const [keyword2, setKeyword2] = useState<string>('KEYWORD');
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | 'squares' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [preserveSpaces, setPreserveSpaces] = useState<boolean>(false);
  const [preservePunctuation, setPreservePunctuation] = useState<boolean>(false);
  const [showSquares, setShowSquares] = useState<boolean>(false);
  const [groupOutput, setGroupOutput] = useState<boolean>(true);
  const [groupSize, setGroupSize] = useState<number>(2);
  const [fillerLetter, setFillerLetter] = useState<string>('X');
  const [jReplacesI, setJReplacesI] = useState<boolean>(true);
  const [orientation, setOrientation] = useState<'horizontal' | 'vertical'>('horizontal');
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0,
    digramsProcessed: 0,
    totalSquaresUsed: 0,
    uniqueKeywords: new Set<string>(),
    averageKeywordLength: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);
  const [twoSquares, setTwoSquares] = useState<string[][][]>([]);

  // Generate 5x5 square from keyword
  const generateSquare = useCallback((keyword: string): string[][] => {
    const cleanKeyword = keyword.replace(/[^A-Z]/g, '').toUpperCase();
    const alphabet = 'ABCDEFGHIKLMNOPQRSTUVWXYZ'; // Note: J is omitted if jReplacesI is true
    let keyString = '';
    const used = new Set<string>();
    
    // Add unique letters from keyword
    for (const char of cleanKeyword) {
      if (!used.has(char) && (char !== 'J' || !jReplacesI)) {
        keyString += char;
        used.add(char);
      }
    }
    
    // Add remaining letters
    for (const char of alphabet) {
      if (!used.has(char) && (char !== 'J' || !jReplacesI)) {
        keyString += char;
        used.add(char);
      }
    }
    
    // Create 5x5 grid
    const square: string[][] = [];
    for (let i = 0; i < 5; i++) {
      square[i] = [];
      for (let j = 0; j < 5; j++) {
        square[i][j] = keyString[i * 5 + j] || '';
      }
    }
    
    return square;
  }, [jReplacesI]);

  // Generate both squares
  const generateTwoSquares = useCallback((key1: string, key2: string): string[][][] => {
    const square1 = generateSquare(key1);
    const square2 = generateSquare(key2);
    
    return [square1, square2];
  }, [generateSquare]);

  // Find position of letter in square
  const findPosition = useCallback((letter: string, square: string[][]): [number, number] => {
    const searchLetter = jReplacesI && letter === 'J' ? 'I' : letter;
    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 5; j++) {
        if (square[i][j] === searchLetter) {
          return [i, j];
        }
      }
    }
    return [-1, -1];
  }, [jReplacesI]);

  // Clean text based on preservation settings
  const cleanText = useCallback((text: string): string => {
    let cleaned = text;
    
    if (!preserveSpaces) {
      cleaned = cleaned.replace(/\s/g, '');
    }
    
    if (!preservePunctuation) {
      cleaned = cleaned.replace(/[^\w\s]/g, '');
    }
    
    if (!preserveCase) {
      cleaned = cleaned.toUpperCase();
    }
    
    return cleaned;
  }, [preserveSpaces, preservePunctuation, preserveCase]);

  // Prepare text for Two Square cipher
  const prepareText = useCallback((text: string): string => {
    let prepared = text.replace(/[^A-Z]/g, '').toUpperCase();
    if (jReplacesI) {
      prepared = prepared.replace(/J/g, 'I');
    }
    
    // Insert filler letters between repeated characters in digrams
    let result = '';
    for (let i = 0; i < prepared.length; i += 2) {
      if (i + 1 < prepared.length) {
        if (prepared[i] === prepared[i + 1]) {
          result += prepared[i] + fillerLetter;
          i--; // Process the second character again
        } else {
          result += prepared[i] + prepared[i + 1];
        }
      } else {
        result += prepared[i] + fillerLetter; // Add filler for odd length
      }
    }
    
    return result;
  }, [fillerLetter, jReplacesI]);

  // Two Square cipher encryption
  const twoSquareEncrypt = useCallback((text: string, key1: string, key2: string): string => {
    if (!text || !key1 || !key2) return '';
    
    const squares = generateTwoSquares(key1, key2);
    setTwoSquares(squares);
    
    const preparedText = prepareText(text);
    let result = '';
    
    // Process digrams
    for (let i = 0; i < preparedText.length; i += 2) {
      const char1 = preparedText[i];
      const char2 = preparedText[i + 1];
      
      if (!char2) break; // Skip incomplete digram
      
      // Find positions in respective squares
      const [row1, col1] = findPosition(char1, squares[0]); // First square
      const [row2, col2] = findPosition(char2, squares[1]); // Second square
      
      if (row1 === -1 || row2 === -1) continue;
      
      let cipher1, cipher2;
      
      if (orientation === 'horizontal') {
        // Horizontal arrangement: use same row, swap columns
        cipher1 = squares[0][row1][col2]; // First square, row of char1, column of char2
        cipher2 = squares[1][row2][col1]; // Second square, row of char2, column of char1
      } else {
        // Vertical arrangement: use same column, swap rows
        cipher1 = squares[0][row2][col1]; // First square, row of char2, column of char1
        cipher2 = squares[1][row1][col2]; // Second square, row of char1, column of char2
      }
      
      result += cipher1 + cipher2;
    }
    
    // Format output
    if (groupOutput && result) {
      const groups = [];
      for (let i = 0; i < result.length; i += groupSize) {
        groups.push(result.slice(i, i + groupSize));
      }
      result = groups.join(' ');
    }
    
    return result;
  }, [generateTwoSquares, prepareText, findPosition, orientation, groupOutput, groupSize]);

  // Two Square cipher decryption
  const twoSquareDecrypt = useCallback((text: string, key1: string, key2: string): string => {
    if (!text || !key1 || !key2) return '';
    
    const squares = generateTwoSquares(key1, key2);
    setTwoSquares(squares);
    
    // Remove spaces if text was grouped
    const cleanedText = text.replace(/\s/g, '');
    let result = '';
    
    // Process digrams
    for (let i = 0; i < cleanedText.length; i += 2) {
      const cipher1 = cleanedText[i];
      const cipher2 = cleanedText[i + 1];
      
      if (!cipher2) break; // Skip incomplete digram
      
      // Find positions in respective squares
      const [row1, col1] = findPosition(cipher1, squares[0]); // First square
      const [row2, col2] = findPosition(cipher2, squares[1]); // Second square
      
      if (row1 === -1 || row2 === -1) continue;
      
      let plain1, plain2;
      
      if (orientation === 'horizontal') {
        // Horizontal arrangement: reverse the process
        plain1 = squares[0][row1][col2]; // First square, row of cipher1, column of cipher2
        plain2 = squares[1][row2][col1]; // Second square, row of cipher2, column of cipher1
      } else {
        // Vertical arrangement: reverse the process
        plain1 = squares[0][row2][col1]; // First square, row of cipher2, column of cipher1
        plain2 = squares[1][row1][col2]; // Second square, row of cipher1, column of cipher2
      }
      
      result += plain1 + plain2;
    }
    
    return result;
  }, [generateTwoSquares, findPosition, orientation]);

  // Process text - main cipher logic
  const processText = useCallback(() => {
    if (!keyword1.trim() || !keyword2.trim()) {
      setOutputText('');
      return '';
    }
    
    const result = operation === 'encrypt' 
      ? twoSquareEncrypt(inputText, keyword1, keyword2)
      : twoSquareDecrypt(inputText, keyword1, keyword2);
    
    setOutputText(result);
    return result;
  }, [inputText, keyword1, keyword2, operation, twoSquareEncrypt, twoSquareDecrypt]);

  // Update analytics
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !keyword1.trim() || !keyword2.trim() || !result) return;
    
    const cleanInput = inputText.replace(/[^A-Z]/g, '').toUpperCase();
    const digramsCount = Math.ceil(cleanInput.length / 2);
    
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      keyword1: keyword1,
      keyword2: keyword2,
      operation,
      timestamp: new Date(),
      textLength: inputText.length,
      digramsCount: digramsCount,
      orientation: orientation
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]);
    
    setStats(prevStats => {
      const newUniqueKeywords = new Set(Array.from(prevStats.uniqueKeywords).concat([keyword1.toUpperCase(), keyword2.toUpperCase()]));
      const totalOps = prevStats.totalOperations + 1;
      const totalChars = prevStats.totalCharactersProcessed + inputText.length;
      
      return {
        ...prevStats,
        totalEncryptions: prevStats.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
        totalDecryptions: prevStats.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
        totalOperations: totalOps,
        totalCharactersProcessed: totalChars,
        averageTextLength: Math.round(totalChars / totalOps),
        longestText: Math.max(prevStats.longestText, inputText.length),
        digramsProcessed: prevStats.digramsProcessed + digramsCount,
        totalSquaresUsed: prevStats.totalSquaresUsed + 2,
        uniqueKeywords: newUniqueKeywords,
        averageKeywordLength: Math.round(Array.from(newUniqueKeywords).reduce((sum, k) => sum + k.length, 0) / newUniqueKeywords.size)
      };
    });
  }, [inputText, keyword1, keyword2, operation, orientation]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText && keyword1 && keyword2) {
      const result = processText();
      if (result) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
    }
  }, [inputText, keyword1, keyword2, operation, orientation]);

  // Update squares when keywords change
  useEffect(() => {
    if (keyword1 && keyword2) {
      setTwoSquares(generateTwoSquares(keyword1, keyword2));
    }
  }, [keyword1, keyword2, generateTwoSquares]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setKeyword1('EXAMPLE');
    setKeyword2('KEYWORD');
    setOperation('encrypt');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText.replace(/\s/g, ''));
    setOutputText(temp);
    setOperation(operation === 'encrypt' ? 'decrypt' : 'encrypt');
  };

  const generateSampleText = () => {
    const samples = [
      "ATTACK AT DAWN FROM THE EAST",
      "THE TWO SQUARE CIPHER IS SECURE",
      "MEET ME AT THE SECRET LOCATION",
      "ENEMY FORCES APPROACHING QUICKLY",
      "MISSION ACCOMPLISHED RETURN HOME",
      "POLYGRAPHIC SUBSTITUTION WORKS",
      "DIGRAPH ENCRYPTION IS EFFECTIVE"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const generateRandomKeywords = () => {
    const words = [
      'EXAMPLE', 'KEYWORD', 'CIPHER', 'SECRET', 'CRYPTO', 'MATRIX',
      'SECURE', 'ENCODE', 'DECODE', 'HIDDEN', 'MYSTERY', 'PUZZLE',
      'DIGRAPH', 'SQUARE', 'DOUBLE', 'TWIN'
    ];
    const randomWord1 = words[Math.floor(Math.random() * words.length)];
    const randomWord2 = words[Math.floor(Math.random() * words.length)];
    setKeyword1(randomWord1);
    setKeyword2(randomWord2);
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Two Square Cipher - Advanced Polygraphic Digraph Encryption Tool | Free Online Cryptography</title>
        <meta name="description" content="Professional Two Square Cipher tool with dual keyword squares, digraph encryption, and historical polygraphic substitution cipher features. Perfect for cryptography education and classical cipher research." />
        <meta name="keywords" content="two square cipher, polygraphic cipher, digraph cipher, dual square cipher, substitution cipher, classical cryptography, two squares encryption, digraph substitution, polygraphic substitution, cipher squares, historical encryption, cryptanalysis, security education" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Two Square Cipher - Advanced Polygraphic Encryption Tool" />
        <meta property="og:description" content="Professional Two Square Cipher with dual keyword squares, digraph processing, and historical polygraphic substitution features." />
        <meta property="og:image" content="/api/placeholder/1200/630" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:site_name" content="Two Square Cipher Tool" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Two Square Cipher - Historical Polygraphic Encryption" />
        <meta name="twitter:description" content="Professional Two Square Cipher with dual squares and educational features. Free classical cryptography tool." />
        <meta name="twitter:image" content="/api/placeholder/1200/630" />
        <meta name="twitter:creator" content="@twosquarecipher" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="Two Square Cipher Tool" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        
        {/* Historical & Cryptographic Keywords */}
        <meta name="subject" content="Classical Cryptography, Polygraphic Ciphers, Historical Encryption" />
        <meta name="classification" content="Education, History, Mathematics, Computer Science" />
        
        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content={isDarkMode ? 'black-translucent' : 'default'} />
        <meta name="apple-mobile-web-app-title" content="Two Square Cipher" />
        
        {/* Theme Color */}
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        <meta name="msapplication-navbutton-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Two Square Cipher Tool",
            "alternateName": ["Two-Square Cipher", "Dual Square Cipher", "Polygraphic Cipher Tool", "Digraph Cipher"],
            "description": "Advanced Two Square Cipher tool for polygraphic digraph encryption with dual keyword squares, substitution visualization, and historical cipher features.",
            "url": typeof window !== 'undefined' ? window.location.href : '',
            "applicationCategory": ["SecurityApplication", "EducationalApplication", "HistoricalApplication"],
            "operatingSystem": "Any",
            "browserRequirements": "HTML5, JavaScript",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Real-time Two Square encryption and decryption",
              "Dual keyword square visualization",
              "Digraph substitution processing",
              "Historical polygraphic cipher implementation",
              "Two squares generation and display",
              "Advanced cipher options and settings",
              "Analytics and usage statistics",
              "Educational cipher information",
              "Dark and light themes",
              "File upload and download",
              "Mobile responsive design"
            ],
            "screenshot": "/api/placeholder/1200/630",
            "softwareVersion": "1.0.0",
            "datePublished": "2025-01-01",
            "dateModified": new Date().toISOString().split('T')[0],
            "author": {
              "@type": "Organization",
              "name": "Two Square Cipher Tool",
              "description": "Historical cryptography and polygraphic cipher education tools"
            },
            "educationalAlignment": {
              "@type": "AlignmentObject",
              "alignmentType": "teaches",
              "educationalFramework": "Classical Cryptography and Mathematical Education",
              "targetName": "Polygraphic Ciphers and Historical Substitution Methods"
            },
            "learningResourceType": "Interactive Tool",
            "educationalUse": ["instruction", "research", "historical study"],
            "typicalAgeRange": "16-99",
            "inLanguage": "en",
            "isAccessibleForFree": true,
            "about": [
              {
                "@type": "Thing",
                "name": "Classical Cryptography"
              },
              {
                "@type": "Thing",
                "name": "Polygraphic Substitution"
              },
              {
                "@type": "Thing",
                "name": "Mathematical Cipher Systems"
              }
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.7",
              "ratingCount": "532",
              "bestRating": "5",
              "worstRating": "1"
            }
          })}
        </script>
        
        {/* Historical Cryptography Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HistoricalDigitalObject",
            "name": "Two Square Cipher Historical Implementation",
            "description": "Digital recreation of the Two Square cipher system, a classical polygraphic substitution cipher using two 5×5 keyword squares for digraph encryption.",
            "dateCreated": "1900s",
            "creator": {
              "@type": "Organization",
              "name": "Classical Cryptographers"
            },
            "about": {
              "@type": "HistoricalEvent",
              "name": "Classical Cryptography Development"
            },
            "culturalContext": "Early 20th Century Mathematical Cryptography",
            "historicalSignificance": "Simplified polygraphic substitution cipher demonstrating dual-square mathematical principles in classical cryptography"
          })}
        </script>
        
        {/* Educational Cryptography Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LearningResource",
            "name": "Two Square Cipher Cryptography Tutorial",
            "description": "Learn about polygraphic substitution ciphers through hands-on interaction with the Two Square cipher system and dual keyword squares.",
            "educationalLevel": "intermediate",
            "teaches": [
              "Polygraphic substitution ciphers",
              "Digraph encryption methods",
              "Dual keyword square construction",
              "Mathematical cipher principles",
              "Classical cryptanalysis techniques"
            ],
            "learningResourceType": "Interactive Tool",
            "interactivityType": "active",
            "educationalUse": ["instruction", "research", "mathematical analysis"],
            "audience": {
              "@type": "EducationalAudience",
              "educationalRole": ["student", "teacher", "mathematician", "researcher"],
              "audienceType": ["cryptography student", "mathematics enthusiast"]
            },
            "about": [
              {
                "@type": "Thing",
                "name": "Mathematical Cryptography"
              },
              {
                "@type": "Thing",
                "name": "Polygraphic Substitution"
              },
              {
                "@type": "Thing",
                "name": "Classical Cipher Analysis"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Grid className="text-teal-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Two Square Cipher</h1>
                <p className="text-sm opacity-60">Polygraphic Digraph Substitution Cipher (1900s)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Squares Viewer */}
              <button
                onClick={() => setActiveModal(activeModal === 'squares' ? null : 'squares')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="View Two Squares"
              >
                <Grid size={20} />
              </button>
              
              {/* Help */}
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              {/* Advanced Options */}
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              {/* Analytics */}
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              {/* Theme Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-7 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="encrypt">🔒 Encrypt</option>
                    <option value="decrypt">🔓 Decrypt</option>
                  </select>
                </div>
                <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="encrypt">🔒 Encrypt</option>
                    <option value="decrypt">🔓 Decrypt</option>
                  </select>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Keyword 1</label>
                    <button
                      onClick={generateRandomKeywords}
                      className={`text-xs px-2 py-1 rounded ${
                        isDarkMode ? 'bg-teal-600 hover:bg-teal-700' : 'bg-teal-500 hover:bg-teal-600'
                      } text-white transition-colors`}
                      title="Generate Random Keywords"
                    >
                      Random
                    </button>
                  </div>
                  <input
                    type="text"
                    value={keyword1}
                    onChange={(e) => setKeyword1(e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase())}
                    placeholder="Enter first keyword..."
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Keyword 2</label>
                  <input
                    type="text"
                    value={keyword2}
                    onChange={(e) => setKeyword2(e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase())}
                    placeholder="Enter second keyword..."
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Orientation</label>
                  <select
                    value={orientation}
                    onChange={(e) => setOrientation(e.target.value as 'horizontal' | 'vertical')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="horizontal">↔ Horizontal</option>
                    <option value="vertical">↕ Vertical</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={swapTexts}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-teal-600 border-teal-500 hover:bg-teal-700 text-white' : 'bg-teal-500 border-teal-400 hover:bg-teal-600 text-white'
                    } transition-colors`}
                    title="Swap input and output"
                  >
                    <Shuffle size={16} />
                    <span>Swap</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleText}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                    title="Generate sample text"
                  >
                    <Key size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
              
              {/* Two Squares Preview */}
              {showSquares && twoSquares.length > 0 && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-3">Two Squares Layout ({orientation}):</h4>
                  <div className={`flex ${orientation === 'horizontal' ? 'flex-row' : 'flex-col'} gap-6 justify-center items-center`}>
                    {/* First Square */}
                    <div className="text-center">
                      <div className="text-xs font-medium mb-2 text-teal-500">Square 1: {keyword1}</div>
                      <div className="grid grid-cols-5 gap-1 w-fit mx-auto">
                        {twoSquares[0]?.flat().map((letter, index) => (
                          <div
                            key={index}
                            className={`w-6 h-6 flex items-center justify-center text-xs font-mono font-bold border ${
                              isDarkMode ? 'bg-teal-700 border-teal-600 text-white' : 'bg-teal-100 border-teal-300 text-teal-900'
                            }`}
                          >
                            {letter}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Orientation Indicator */}
                    <div className="text-2xl text-gray-400">
                      {orientation === 'horizontal' ? '↔' : '↕'}
                    </div>
                    
                    {/* Second Square */}
                    <div className="text-center">
                      <div className="text-xs font-medium mb-2 text-cyan-500">Square 2: {keyword2}</div>
                      <div className="grid grid-cols-5 gap-1 w-fit mx-auto">
                        {twoSquares[1]?.flat().map((letter, index) => (
                          <div
                            key={index}
                            className={`w-6 h-6 flex items-center justify-center text-xs font-mono font-bold border ${
                              isDarkMode ? 'bg-cyan-700 border-cyan-600 text-white' : 'bg-cyan-100 border-cyan-300 text-cyan-900'
                            }`}
                          >
                            {letter}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs opacity-60 mt-3 text-center">
                    {orientation === 'horizontal' 
                      ? "Digraphs are encrypted using same rows, swapping columns between squares"
                      : "Digraphs are encrypted using same columns, swapping rows between squares"
                    }
                  </div>
                </div>
              )}
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Input Text</h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter text to encrypt or decrypt using Two Square cipher..."
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
                <div className="mt-2 flex items-center space-x-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileUpload}
                      className="sr-only"
                    />
                    <Upload size={16} className="mr-1" />
                    <span className="text-sm">Upload File</span>
                  </label>
                </div>
              </div>

              {/* Output */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Output Text</h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'twosquare-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>
          {/* </div> */}
        </main>

        {/* Two Squares Modal */}
        {activeModal === 'squares' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Grid className="text-teal-500" />
                  <span>Two Squares Visualization</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6">
                {twoSquares.length > 0 && (
                  <>
                    <div className="text-center">
                      <div className="text-sm opacity-60 mb-4">
                        Keywords: <strong>{keyword1}</strong> and <strong>{keyword2}</strong> | 
                        Orientation: <strong>{orientation}</strong>
                      </div>
                    </div>
                    
                    {/* Large Two Squares Display */}
                    <div className={`flex ${orientation === 'horizontal' ? 'flex-row' : 'flex-col'} gap-12 justify-center items-center`}>
                      {/* First Square */}
                      <div className="text-center">
                        <div className="text-lg font-semibold mb-3 text-teal-600">Square 1: {keyword1}</div>
                        <div className="grid grid-cols-5 gap-2 w-fit mx-auto">
                          {twoSquares[0]?.flat().map((letter, index) => (
                            <div
                              key={index}
                              className={`w-10 h-10 flex items-center justify-center text-sm font-mono font-bold border-2 ${
                                isDarkMode ? 'bg-teal-700 border-teal-600 text-white' : 'bg-teal-100 border-teal-400 text-teal-900'
                              }`}
                            >
                              {letter}
                            </div>
                          ))}
                        </div>
                        <div className="text-xs opacity-60 mt-2">First letter of digraph</div>
                      </div>
                      
                      {/* Orientation Indicator */}
                      <div className="text-6xl text-gray-400">
                        {orientation === 'horizontal' ? '↔' : '↕'}
                      </div>
                      
                      {/* Second Square */}
                      <div className="text-center">
                        <div className="text-lg font-semibold mb-3 text-cyan-600">Square 2: {keyword2}</div>
                        <div className="grid grid-cols-5 gap-2 w-fit mx-auto">
                          {twoSquares[1]?.flat().map((letter, index) => (
                            <div
                              key={index}
                              className={`w-10 h-10 flex items-center justify-center text-sm font-mono font-bold border-2 ${
                                isDarkMode ? 'bg-cyan-700 border-cyan-600 text-white' : 'bg-cyan-100 border-cyan-400 text-cyan-900'
                              }`}
                            >
                              {letter}
                            </div>
                          ))}
                        </div>
                        <div className="text-xs opacity-60 mt-2">Second letter of digraph</div>
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-sm opacity-60 space-y-2">
                        <div><strong>{orientation === 'horizontal' ? 'Horizontal' : 'Vertical'} Encryption Process:</strong></div>
                        {orientation === 'horizontal' ? (
                          <>
                            <div>1. Find first letter of digraph in Square 1 (row R1, column C1)</div>
                            <div>2. Find second letter of digraph in Square 2 (row R2, column C2)</div>
                            <div>3. First cipher letter: Square 1 at position (R1, C2)</div>
                            <div>4. Second cipher letter: Square 2 at position (R2, C1)</div>
                          </>
                        ) : (
                          <>
                            <div>1. Find first letter of digraph in Square 1 (row R1, column C1)</div>
                            <div>2. Find second letter of digraph in Square 2 (row R2, column C2)</div>
                            <div>3. First cipher letter: Square 1 at position (R2, C1)</div>
                            <div>4. Second cipher letter: Square 2 at position (R1, C2)</div>
                          </>
                        )}
                        <div>5. Combine both cipher letters to form encrypted digraph</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Shield className="text-teal-500" />
                  <span>About Two Square Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is the Two Square Cipher?</h3>
                  <p>The Two Square Cipher is a polygraphic substitution cipher that encrypts pairs of letters (digraphs) using two 5×5 keyword squares. It's a simplified version of the Four Square cipher, offering a good balance between security and simplicity. The cipher operates by finding letters in one square and replacing them with letters from corresponding positions in the other square.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How it Works</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Create two 5×5 squares using different keywords</li>
                    <li>Arrange squares horizontally or vertically</li>
                    <li>Split plaintext into pairs of letters (digraphs)</li>
                    <li>Find first letter in Square 1, second letter in Square 2</li>
                    <li>Create cipher by swapping positions between squares</li>
                    <li>Combine cipher letters to form encrypted digraph</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Orientation Methods</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="text-xs space-y-2">
                      <div><strong>Horizontal Arrangement:</strong></div>
                      <div>• Squares arranged side by side</div>
                      <div>• Use same rows, swap columns</div>
                      <div>• Cipher₁ = Square₁[row₁][col₂]</div>
                      <div>• Cipher₂ = Square₂[row₂][col₁]</div>
                      <div className="mt-2"><strong>Vertical Arrangement:</strong></div>
                      <div>• Squares arranged top and bottom</div>
                      <div>• Use same columns, swap rows</div>
                      <div>• Cipher₁ = Square₁[row₂][col₁]</div>
                      <div>• Cipher₂ = Square₂[row₁][col₂]</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Encryption Example</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="font-mono text-xs space-y-1">
                      <div><strong>Keywords:</strong> EXAMPLE, KEYWORD</div>
                      <div><strong>Plaintext:</strong> "HELLO"</div>
                      <div><strong>Digraphs:</strong> HE LL OX</div>
                      <div><strong>Process for "HE" (horizontal):</strong></div>
                      <div>• Find H in Square 1 (EXAMPLE) → row 1, col 2</div>
                      <div>• Find E in Square 2 (KEYWORD) → row 2, col 0</div>
                      <div>• Cipher H: Square 1[1][0] = ?</div>
                      <div>• Cipher E: Square 2[2][2] = ?</div>
                      <div><strong>Result:</strong> Encrypted digraph</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Historical Background</h3>
                  <ul className="space-y-1">
                    <li><strong>Era:</strong> Early 20th century</li>
                    <li><strong>Type:</strong> Polygraphic substitution cipher</li>
                    <li><strong>Purpose:</strong> Simplified alternative to Four Square</li>
                    <li><strong>Usage:</strong> Military and diplomatic communications</li>
                    <li><strong>Advantage:</strong> Easier to implement than four squares</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Encryption Rules</h3>
                  <ul className="space-y-1">
                    <li><strong>Digraph Processing:</strong> Always encrypt two letters at a time</li>
                    <li><strong>Position Swapping:</strong> Exchange coordinates between squares</li>
                    <li><strong>Orientation Matters:</strong> Horizontal vs vertical affects method</li>
                    <li><strong>Filler Letters:</strong> Insert X between repeated letters</li>
                    <li><strong>Odd Length:</strong> Add filler letter for incomplete pairs</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Advantages</h3>
                  <ul className="space-y-1">
                    <li>Stronger than monoalphabetic ciphers</li>
                    <li>Simpler than Four Square cipher</li>
                    <li>Disguises letter frequency patterns</li>
                    <li>Two keywords provide good security</li>
                    <li>Can be performed manually</li>
                    <li>Flexible orientation options</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Weaknesses</h3>
                  <ul className="space-y-1">
                    <li>Vulnerable to digraph frequency analysis</li>
                    <li>Less secure than Four Square</li>
                    <li>Known plaintext attacks possible</li>
                    <li>Pattern analysis can reveal structure</li>
                    <li>Limited by 5×5 square constraints</li>
                    <li>Only two keyword variations</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Comparison with Other Ciphers</h3>
                  <ul className="space-y-1">
                    <li><strong>vs Four Square:</strong> Simpler but less secure</li>
                    <li><strong>vs Playfair:</strong> More flexible with two independent squares</li>
                    <li><strong>vs Caesar:</strong> Much more sophisticated substitution</li>
                    <li><strong>vs Vigenère:</strong> Different approach, digraph vs polyalphabetic</li>
                    <li><strong>vs Simple Substitution:</strong> Polygraphic provides better security</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Variations</h3>
                  <ul className="space-y-1">
                    <li><strong>Orientation Variants:</strong> Horizontal vs vertical arrangements</li>
                    <li><strong>Three Square:</strong> Adding a third square for complexity</li>
                    <li><strong>Progressive Keys:</strong> Changing keywords during encryption</li>
                    <li><strong>Block Cipher:</strong> Multiple Two Square operations</li>
                    <li><strong>Combined Systems:</strong> Two Square with other ciphers</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Cryptanalysis Methods</h3>
                  <ul className="space-y-1">
                    <li><strong>Digraph Frequency:</strong> Analyze most common letter pairs</li>
                    <li><strong>Known Plaintext:</strong> Use known message segments</li>
                    <li><strong>Pattern Recognition:</strong> Look for repeated digraphs</li>
                    <li><strong>Keyword Recovery:</strong> Deduce keywords from patterns</li>
                    <li><strong>Statistical Analysis:</strong> Study letter position relationships</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Modern Applications</h3>
                  <ul className="space-y-1">
                    <li>Cryptography education and training</li>
                    <li>Introduction to polygraphic ciphers</li>
                    <li>Historical cipher demonstrations</li>
                    <li>Understanding dual-key systems</li>
                    <li>Puzzle creation and recreational cryptography</li>
                    <li>Classical cryptanalysis practice</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Implementation Tips</h3>
                  <ul className="space-y-1">
                    <li><strong>Keyword Selection:</strong> Use unrelated, unique keywords</li>
                    <li><strong>Orientation Choice:</strong> Horizontal generally preferred</li>
                    <li><strong>Text Preparation:</strong> Handle repeated letters carefully</li>
                    <li><strong>Error Prevention:</strong> Double-check square construction</li>
                    <li><strong>Security Practice:</strong> Keep keywords secret and separate</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Level</h3>
                  <p className="text-orange-600">⚠️ <strong>Historical Security:</strong> The Two Square cipher provided good security for its era, being significantly stronger than simple substitution ciphers while remaining more practical than the Four Square cipher. However, modern cryptanalytic techniques and computer analysis can break it relatively easily. It's excellent for education and understanding polygraphic substitution principles but not suitable for actual security needs.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show Two Squares</label>
                  <input
                    type="checkbox"
                    checked={showSquares}
                    onChange={(e) => setShowSquares(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Group Output</label>
                  <input
                    type="checkbox"
                    checked={groupOutput}
                    onChange={(e) => setGroupOutput(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">J replaces I</label>
                  <input
                    type="checkbox"
                    checked={jReplacesI}
                    onChange={(e) => setJReplacesI(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                {groupOutput && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Group Size</label>
                    <input
                      type="range"
                      min="2"
                      max="5"
                      value={groupSize}
                      onChange={(e) => setGroupSize(parseInt(e.target.value))}
                      className="w-full"
                    />
                    <div className="text-xs opacity-60 mt-1">
                      Characters per group: {groupSize}
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium mb-2">Filler Letter</label>
                  <input
                    type="text"
                    value={fillerLetter}
                    onChange={(e) => setFillerLetter(e.target.value.replace(/[^A-Z]/g, '').slice(0, 1) || 'X')}
                    placeholder="X"
                    maxLength={1}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                  <div className="text-xs opacity-60 mt-1">
                    Letter inserted between repeated characters in digrams
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Orientation</h3>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="orientation"
                        value="horizontal"
                        checked={orientation === 'horizontal'}
                        onChange={(e) => setOrientation(e.target.value as 'horizontal')}
                        className="mr-2"
                      />
                      <span className="text-sm">Horizontal (↔ side by side)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="orientation"
                        value="vertical"
                        checked={orientation === 'vertical'}
                        onChange={(e) => setOrientation(e.target.value as 'vertical')}
                        className="mr-2"
                      />
                      <span className="text-sm">Vertical (↕ top and bottom)</span>
                    </label>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Historical Keywords</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {[['EXAMPLE', 'KEYWORD'], ['CIPHER', 'SECRET'], ['MATRIX', 'CRYPTO'], ['SQUARE', 'DOUBLE'], ['TWIN', 'DUAL'], ['ENCODE', 'DECODE']].map(([key1, key2], index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setKeyword1(key1);
                          setKeyword2(key2);
                        }}
                        className={`p-2 text-xs rounded border ${
                          isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                        } transition-colors`}
                      >
                        {key1}/{key2}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Cipher Presets</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setKeyword1('EXAMPLE');
                        setKeyword2('KEYWORD');
                        setOrientation('horizontal');
                        setGroupOutput(true);
                        setGroupSize(2);
                        setFillerLetter('X');
                        setJReplacesI(true);
                        setShowSquares(true);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-teal-600 border-teal-500 hover:bg-teal-700 text-white' : 'bg-teal-500 border-teal-400 hover:bg-teal-600 text-white'
                      } transition-colors`}
                    >
                      Classical Standard
                    </button>
                    
                    <button
                      onClick={() => {
                        setKeyword1('TWIN');
                        setKeyword2('SQUARE');
                        setOrientation('vertical');
                        setGroupOutput(true);
                        setGroupSize(2);
                        setFillerLetter('Q');
                        setJReplacesI(true);
                        setShowSquares(true);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-cyan-600 border-cyan-500 hover:bg-cyan-700 text-white' : 'bg-cyan-500 border-cyan-400 hover:bg-cyan-600 text-white'
                      } transition-colors`}
                    >
                      Vertical Variant
                    </button>
                    
                    <button
                      onClick={() => {
                        setKeyword1('CIPHER');
                        setKeyword2('MATRIX');
                        setOrientation('horizontal');
                        setGroupOutput(false);
                        setFillerLetter('Z');
                        setJReplacesI(false);
                        setShowSquares(false);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                      } transition-colors`}
                    >
                      Modern Educational
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                  <div className="text-sm">Total Encryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                  <div className="text-sm">Total Decryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.digramsProcessed}</div>
                  <div className="text-sm">Digrams Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.totalCharactersProcessed}</div>
                  <div className="text-sm">Characters Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.uniqueKeywords.size}</div>
                  <div className="text-sm">Unique Keywords</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.averageKeywordLength}</div>
                  <div className="text-sm">Avg Keyword Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-amber-500">{stats.totalSquaresUsed}</div>
                  <div className="text-sm">Squares Used</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-red-500">{stats.longestText}</div>
                  <div className="text-sm">Longest Text</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation.toUpperCase()}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            {item.digramsCount} digrams
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-teal-500 text-white">
                            {item.keyword1}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-cyan-500 text-white">
                            {item.keyword2}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-gray-500 text-white">
                            {item.orientation === 'horizontal' ? '↔' : '↕'}
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono">{item.input}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <Grid size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start encrypting or decrypting to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
              
              {history.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Cipher Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-purple-500">Most Used Keywords:</div>
                      <div>
                        {history.length > 0 
                          ? (() => {
                              const keywordCounts = history.reduce((acc, item) => {
                                const key = `${item.keyword1}/${item.keyword2}`;
                                acc[key] = (acc[key] || 0) + 1;
                                return acc;
                              }, {} as Record<string, number>);
                              return Object.keys(keywordCounts).reduce((a, b) => keywordCounts[a] > keywordCounts[b] ? a : b);
                            })()
                          : 'N/A'
                        }
                      </div>
                    </div>
                    <div>
                      <div className="text-green-500">Encrypt/Decrypt Ratio:</div>
                      <div>{stats.totalOperations > 0 ? Math.round((stats.totalEncryptions / stats.totalOperations) * 100) : 0}% Encrypt</div>
                    </div>
                    <div>
                      <div className="text-blue-500">Preferred Orientation:</div>
                      <div>
                        {history.length > 0 
                          ? (() => {
                              const horizontalCount = history.filter(h => h.orientation === 'horizontal').length;
                              return horizontalCount > history.length / 2 ? 'Horizontal' : 'Vertical';
                            })()
                          : 'N/A'
                        }
                      </div>
                    </div>
                    <div>
                      <div className="text-orange-500">Polygraphic Efficiency:</div>
                      <div>{stats.totalCharactersProcessed > 0 ? Math.round((stats.digramsProcessed * 2 / stats.totalCharactersProcessed) * 100) : 0}% Paired</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TwoSquareCipherApp;